import { useEffect, useRef } from "react";
import { useQueryClient, InfiniteData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { Database } from "@/integrations/supabase/types";
import { PaginatedResponse } from "@/types";
import {
  RecordWithTemplate,
  recordService,
} from "@/integrations/records/recordService";
import { useTranslation } from "react-i18next";

type ProcessingStatus = Database["public"]["Enums"]["processing_status"];
type SupabaseRecord = Database["public"]["Tables"]["records"]["Row"];
type Template = Database["public"]["Tables"]["templates"]["Row"];


export const useRecordsRealtime = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { addNotification } = useNotifications();

  // Используем ref для отслеживания последних статусов записей
  const recordStatusesRef = useRef<Record<string, string>>({});

  // Функция для обновления записи в кэше
  const updateRecordInCache = (
    queryKey: readonly unknown[],
    newRecord: SupabaseRecord & { templates: Template | null }
  ): boolean => {
    try {
      const cachedData =
        queryClient.getQueryData<PaginatedResponse<RecordWithTemplate>>(
          queryKey
        );

      if (!cachedData?.data) return false;

      // Ищем запись в кэшированных данных
      const recordIndex = cachedData.data.findIndex(
        (r) => r.id === newRecord.id
      );

      if (recordIndex === -1) return false;

      // Создаем новый массив с обновленной записью
      const updatedData = [...cachedData.data];

      // Трансформируем запись в формат приложения
      const transformedRecord = {
        ...updatedData[recordIndex],
        processingStatus: newRecord.processing_status,
        processing_status_desc: newRecord.processing_status_desc,
        processingStartedAt: newRecord.processing_started_at,
        processingFinishedAt: newRecord.processing_finished_at,
      };

      updatedData[recordIndex] = transformedRecord;

      // Обновляем кэш с новыми данными
      queryClient.setQueryData(queryKey, {
        ...cachedData,
        data: updatedData,
      });

      return true;
    } catch (error) {
      console.error("Ошибка при обновлении кэша:", error);
      return false;
    }
  };

  useEffect(() => {
    if (!user) return;

    // Подписываемся на изменения в таблице records для текущего пользователя
    const subscription = supabase
      .channel("records-changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "records",
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          const oldRecord = payload.old as SupabaseRecord;
          const newRecord = payload.new as SupabaseRecord;

          // Получаем последний известный статус из нашего ref
          const lastKnownStatus = recordStatusesRef.current[newRecord.id];

          // Проверяем, изменился ли статус обработки
          if (newRecord.processing_status !== lastKnownStatus) {
            console.log(
              `🔄 REALTIME: Статус записи "${newRecord.name}" изменился на "${newRecord.processing_status}"`
            );

            // Обновляем последний известный статус
            recordStatusesRef.current[newRecord.id] =
              newRecord.processing_status;

            // Получаем все активные queries с ключами ["records"] и ["records-infinite"]
            const recordsQueries = queryClient.getQueriesData<
              PaginatedResponse<RecordWithTemplate>
            >({
              queryKey: ["records"],
              type: "active",
            });

            const infiniteRecordsQueries = queryClient.getQueriesData<
              InfiniteData<PaginatedResponse<RecordWithTemplate>>
            >({
              queryKey: ["records-infinite"],
              type: "active",
            });

            let updatedInCache = false;

            // Получаем связанный шаблон для корректного обновления
            const { data: templates } = await supabase
              .from("templates")
              .select("*")
              .eq("id", newRecord.template_id)
              .single();

            const recordWithTemplate = {
              ...newRecord,
              templates: templates || null,
            };

            // Пробуем обновить запись в каждом активном кэше для обычных queries
            for (const [queryKey] of recordsQueries) {
              if (updateRecordInCache(queryKey, recordWithTemplate)) {
                updatedInCache = true;
                console.log(
                  `🔄 REALTIME: Обновили запись ${newRecord.id
                  } в кэше для ${JSON.stringify(queryKey)}`
                );
              }
            }

            // Обновляем infinite queries
            for (const [queryKey, queryData] of infiniteRecordsQueries) {
              if (queryData?.pages) {
                const updatedPages = queryData.pages.map((page) => {
                  if (page?.data) {
                    const recordIndex = page.data.findIndex((r) => r.id === newRecord.id);
                    if (recordIndex !== -1) {
                      const updatedData = [...page.data];
                      updatedData[recordIndex] = {
                        ...updatedData[recordIndex],
                        processingStatus: newRecord.processing_status,
                        processing_status_desc: newRecord.processing_status_desc,
                        processingStartedAt: newRecord.processing_started_at,
                        processingFinishedAt: newRecord.processing_finished_at,
                      };
                      return { ...page, data: updatedData };
                    }
                  }
                  return page;
                });

                queryClient.setQueryData(queryKey, {
                  ...queryData,
                  pages: updatedPages,
                });
                updatedInCache = true;
                console.log(
                  `🔄 REALTIME: Обновили запись ${newRecord.id} в infinite query ${JSON.stringify(queryKey)}`
                );
              }
            }

            // Если не смогли обновить в кэше - инвалидируем текущую страницу
            if (!updatedInCache) {
              console.log(
                `🔄 REALTIME: Не нашли запись в кэше, инвалидируем записи`
              );
              queryClient.invalidateQueries({
                queryKey: ["records"],
                type: "active",
                refetchType: "all",
              });
              queryClient.invalidateQueries({
                queryKey: ["records-infinite"],
                type: "active",
                refetchType: "all",
              });
            }

            // Всегда инвалидируем детальную страницу записи
            queryClient.invalidateQueries({
              queryKey: ["record", newRecord.id],
              exact: true,
            });

            // Отправляем уведомление пользователю
            addNotification({
              userId: user.id,
              type:
                newRecord.processing_status === "failed" ? "error" : "success",
              message: `${newRecord.name}: ${t(`records.status_messages.${newRecord.processing_status}`) ||
                t('records.status_messages.changed', { status: newRecord.processing_status })
                }`,
            });
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, queryClient, addNotification]);
};
